import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock, User, Flame, Globe, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

// Glass Card Styles (injected)
const GlassStyles = () => (
  <style>{`
    .content-row-glass {
      --glass-bg: rgba(255,255,255,0.04);
      --glass-border: rgba(255,255,255,0.08);
      --accent-east: #dc2626;
      --accent-west: #06b6d4;
      --accent-neutral: #a78bfa;
    }
    
    .content-row-glass .glass-card {
      background: var(--glass-bg);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid var(--glass-border);
      border-radius: 16px;
      transition: all 0.28s ease;
    }
    
    .content-row-glass .glass-card:hover {
      background: rgba(255,255,255,0.07);
      border-color: rgba(255,255,255,0.14);
      transform: translateY(-6px);
      box-shadow: 0 20px 60px rgba(0,0,0,0.45);
    }
    
    .content-row-glass .glass-card-east:hover {
      border-color: rgba(220,38,38,0.3);
      box-shadow: 0 20px 60px rgba(220,38,38,0.2);
    }
    
    .content-row-glass .glass-card-west:hover {
      border-color: rgba(6,182,212,0.3);
      box-shadow: 0 20px 60px rgba(6,182,212,0.2);
    }
    
    .content-row-glass .scroll-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.1);
      color: rgba(255,255,255,0.6);
      transition: all 0.25s ease;
    }
    
    .content-row-glass .scroll-btn:hover:not(:disabled) {
      background: rgba(255,255,255,0.1);
      border-color: rgba(255,255,255,0.2);
      color: #fff;
      transform: scale(1.05);
    }
    
    .content-row-glass .scroll-btn:disabled {
      opacity: 0.25;
      cursor: not-allowed;
    }
    
    .content-row-glass .section-label {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    
    .content-row-glass .label-bar {
      width: 3px;
      height: 24px;
      border-radius: 2px;
    }
    
    .content-row-glass .label-text {
      font-family: 'Azonix', 'Impact', sans-serif;
      font-size: 16px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }
    
    .content-row-glass .card-image-container {
      position: relative;
      overflow: hidden;
      border-radius: 16px 16px 0 0;
    }
    
    .content-row-glass .card-image-container img {
      transition: transform 0.5s ease;
    }
    
    .content-row-glass .glass-card:hover .card-image-container img {
      transform: scale(1.06);
    }
    
    .content-row-glass .card-badge {
      position: absolute;
      top: 12px;
      left: 12px;
      padding: 4px 10px;
      border-radius: 6px;
      font-family: 'Azonix', 'Impact', sans-serif;
      font-size: 9px;
      letter-spacing: 1.5px;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }
    
    .content-row-glass .card-badge-east {
      background: rgba(220,38,38,0.2);
      border: 1px solid rgba(220,38,38,0.3);
      color: #dc2626;
    }
    
    .content-row-glass .card-badge-west {
      background: rgba(6,182,212,0.2);
      border: 1px solid rgba(6,182,212,0.3);
      color: #06b6d4;
    }
    
    .content-row-glass .card-badge-neutral {
      background: rgba(167,139,250,0.2);
      border: 1px solid rgba(167,139,250,0.3);
      color: #a78bfa;
    }
    
    .content-row-glass .card-title {
      font-family: -apple-system, 'Helvetica Neue', sans-serif;
      font-size: 15px;
      font-weight: 600;
      color: #fff;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    
    .content-row-glass .card-excerpt {
      font-family: -apple-system, 'Helvetica Neue', sans-serif;
      font-size: 13px;
      color: rgba(255,255,255,0.45);
      line-height: 1.6;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    
    .content-row-glass .card-meta {
      display: flex;
      align-items: center;
      gap: 16px;
      font-size: 11px;
      color: rgba(255,255,255,0.35);
    }
    
    .content-row-glass .card-meta span {
      display: flex;
      align-items: center;
      gap: 5px;
    }
    
    .content-row-glass .featured-tag {
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 1px;
      background: rgba(234,179,8,0.15);
      border: 1px solid rgba(234,179,8,0.3);
      color: #eab308;
    }
    
    /* Hide scrollbar but keep functionality */
    .content-row-glass .scroll-container {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    
    .content-row-glass .scroll-container::-webkit-scrollbar {
      display: none;
    }
  `}</style>
);

// SectionLabel component - Outrange style
const SectionLabel = ({ children, accent = '#a78bfa', icon: Icon = Globe }) => (
  <div className="section-label">
    <div 
      className="label-bar" 
      style={{ background: `linear-gradient(to bottom, ${accent}, transparent)` }} 
    />
    <Icon size={12} color={accent} />
    <span 
      className="label-text" 
      style={{ color: accent }}
    >
      {children}
    </span>
  </div>
);

const ContentRow = ({ title, articles, category = 'neutral' }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const scroll = (direction) => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8;
      const newScrollLeft =
        container.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);

      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  useEffect(() => {
    handleScroll();
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [articles]);

  const getThemeClasses = () => {
    switch (category) {
      case 'east':
        return {
          accent: '#dc2626',
          Icon: Flame,
          cardClass: 'glass-card glass-card-east',
          badgeClass: 'card-badge card-badge-east',
        };
      case 'west':
        return {
          accent: '#06b6d4',
          Icon: Star,
          cardClass: 'glass-card glass-card-west',
          badgeClass: 'card-badge card-badge-west',
        };
      default:
        return {
          accent: '#a78bfa',
          Icon: Globe,
          cardClass: 'glass-card',
          badgeClass: 'card-badge card-badge-neutral',
        };
    }
  };

  const theme = getThemeClasses();

  if (!articles || articles.length === 0) return null;

  const getBadgeText = () => {
    switch (category) {
      case 'east': return 'ANIME';
      case 'west': return 'MOVIES';
      default: return 'FEATURED';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="content-row-glass relative mb-16"
    >
      <GlassStyles />
      
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6 px-2">
        <SectionLabel accent={theme.accent} icon={theme.Icon}>
          {typeof title === 'string' ? title : 'Section'}
        </SectionLabel>

        <div className="flex gap-2">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="scroll-btn"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="scroll-btn"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto gap-5 pb-4 scroll-container px-2"
      >
        {articles.map((article, index) => (
          <motion.div
            key={article.id || index}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.08, duration: 0.4 }}
            className="flex-none w-72"
          >
            <Link to={`/article/${article.slug}`}>
              <div className={`${theme.cardClass} h-[380px] flex flex-col overflow-hidden`}>
                {/* Article Image */}
                <div className="card-image-container h-44 relative">
                  {article.featured_image ? (
                    <img
                      src={article.featured_image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                      <div className="text-gray-600 text-3xl font-azonix opacity-25">
                        {category === 'east' ? 'EAST' : category === 'west' ? 'WEST' : 'ANIMAC'}
                      </div>
                    </div>
                  )}

                  {/* Category Badge */}
                  <div className={theme.badgeClass}>
                    {getBadgeText()}
                  </div>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col justify-between flex-1">
                  <div>
                    <h3 className="card-title mb-2">
                      {article.title}
                    </h3>

                    <p className="card-excerpt">
                      {article.excerpt}
                    </p>
                  </div>

                  {/* Article Meta */}
                  <div className="mt-4 pt-3 border-t border-white/5">
                    <div className="card-meta">
                      <span>
                        <User size={11} />
                        {article.author}
                      </span>
                      <span>
                        <Clock size={11} />
                        {article.read_time} min
                      </span>

                      {article.is_featured && (
                        <span className="featured-tag">FEATURED</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Link>

          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default ContentRow;
